<script lang="ts">
  import { Link, Route, Router, navigate, useLocation } from 'svelte-routing';
  import { acting, currentUser, isAuthenticated, managedBy } from '../../lib/stores/auth';
  import Profile from './Profile.svelte';
  import Phones from './Phones.svelte';
  import History from './History.svelte';
  import Appearance from './Appearance.svelte';
  import Activities from './Activities.svelte';
  import Admin from './Admin.svelte';
  import Safety from './Safety.svelte';
  import Forwarding from './Forwarding.svelte';
  import KidsMode from '../../lib/components/kids/KidsMode.svelte';
  import SharingSections from '../../lib/components/sharing/SharingSections.svelte';
  import ApiKeys from '../../lib/components/ApiKeys.svelte';
  import { api } from '../../lib/api/client';
  import type { MapInfo } from '../../lib/api/types';
  import { mapCredits, setMapCredits } from '../../lib/stores/mapCredits';
  import { t } from '../../lib/i18n/index.ts';

  // Settings lists its topics down the side in labeled sections, each at its
  // own address, and in a narrow window the list is the page (spec 010
  // FR-077, 010 FR-078). A child sees each topic read only and a parent
  // acting sees what they may change (010 FR-079).
  type Topic = { path: string; icon: string; label: string };
  type Section = { label: string; topics: Topic[] };

  const location = useLocation();
  // rest is the topic in the address, '' at Settings itself.
  const rest = $derived($location.pathname.replace(/^\/settings\/?/, '').replace(/\/$/, ''));

  const sections = $derived.by((): Section[] => {
    const admin = !!$currentUser?.admin && !$acting;
    // A child's account has no forwarding, and a parent acting cannot add any (spec 012 FR-050).
    const forwarding = !$managedBy && !$acting;
    const list: Section[] = [
      {
        label: t('sectionsAccount'),
        topics: [
          { path: 'profile', icon: 'la-user', label: t('sectionsProfile') },
          { path: 'kids', icon: 'la-child', label: t('sectionsKids') },
          { path: 'devices', icon: 'la-mobile', label: t('sectionsDevices') },
          { path: 'api-keys', icon: 'la-key', label: t('apiKeysTitle') },
        ],
      },
      {
        label: t('sectionsSharing'),
        topics: [
          { path: 'people', icon: 'la-user-friends', label: t('sectionsPeople') },
          ...(forwarding ? [{ path: 'forwarding', icon: 'la-globe', label: t('sectionsForwarding') }] : []),
        ],
      },
      {
        label: t('sectionsSafetyActivities'),
        topics: [
          { path: 'safety', icon: 'la-shield-alt', label: t('sectionsSafety') },
          { path: 'activities', icon: 'la-running', label: t('sectionsActivities') },
        ],
      },
      { label: t('sectionsYourData'), topics: [{ path: 'history', icon: 'la-history', label: t('sectionsHistory') }] },
      { label: t('sectionsThisBrowser'), topics: [{ path: 'appearance', icon: 'la-palette', label: t('sectionsAppearance') }] },
    ];
    if (admin) {
      list.push({
        label: t('sectionsAdministration'),
        topics: [
          { path: 'server', icon: 'la-server', label: t('sectionsServer') },
          { path: 'accounts', icon: 'la-users', label: t('sectionsAccounts') },
        ],
      });
    }
    return list;
  });

  // open is the topic showing: Profile at Settings itself.
  const open = $derived(rest === '' ? 'profile' : rest.split('/')[0]);
  const known = $derived(sections.some((s) => s.topics.some((tp) => tp.path === open)));

  // Credit: addresses and place names show away from any map, so the credit
  // the maps carry is given here too (spec 009 FR-001). It stays whatever
  // the switch below says (spec 009 FR-037).
  const credit = t('settingsMapCredit', { contributors: '\u0000' }).split('\u0000');

  // Map credits may be hidden only while the server's credit is
  // OpenStreetMap's alone (spec 009 FR-038).
  let creditsOptional = $state(false);
  $effect(() => {
    if ($isAuthenticated) api.get<MapInfo>('/map').then((i) => (creditsOptional = !!i.attribution_optional)).catch(() => {});
  });

  $effect(() => {
    if (!$isAuthenticated) navigate('/login', { replace: true });
  });

  // An address that names no topic, such as one this member may not use,
  // shows Settings (010 FR-078).
  $effect(() => {
    if ($currentUser && rest !== '' && !known) navigate('/settings', { replace: true });
  });
</script>

<div class="flex-1 w-full max-w-6xl mx-auto md:flex md:gap-6 p-4 sm:p-6">
  <!-- At Settings itself a narrow window shows only the list; at a topic only the topic. -->
  <nav aria-label={t('appSettings')} class="{rest === '' ? 'block' : 'hidden'} md:block md:w-56 shrink-0 mb-6 md:mb-0">
    {#each sections as s (s.label)}
      <p class="text-xs uppercase tracking-wide text-text-muted mt-4 first:mt-0 mb-1 px-3">{s.label}</p>
      <ul class="topics">
        {#each s.topics as tp (tp.path)}
          <li>
            <Link to="/settings/{tp.path}"
                  class="topic flex items-center gap-3 px-3 py-3 md:py-1.5 text-sm {open === tp.path ? 'open' : ''}"
                  aria-current={open === tp.path ? 'page' : undefined}>
              <i aria-hidden="true" class="las {tp.icon} text-lg"></i>
              <span class="flex-1">{tp.label}</span>
              <i aria-hidden="true" class="las la-angle-right text-text-muted md:hidden"></i>
            </Link>
          </li>
        {/each}
      </ul>
    {/each}
    <!-- The credit closes the list, so it stays put whichever topic is open (010 FR-077). -->
    <p class="text-xs text-text-muted mt-6 px-3">
      {credit[0]}<a class="underline" href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">{t('settingsMapCreditContributors')}</a>{credit[1]}
    </p>
    {#if creditsOptional}
      <label class="flex items-start gap-2 text-xs text-text-muted mt-2 px-3">
        <input type="checkbox" class="mt-0.5" checked={$mapCredits} onchange={(e) => setMapCredits(e.currentTarget.checked)} />
        {t('settingsShowMapCredits')}
      </label>
    {/if}
  </nav>

  <main class="{rest === '' ? 'hidden' : 'block'} md:block flex-1 min-w-0">
    <Link to="/settings" class="md:hidden inline-flex items-center gap-1 text-sm mb-4 hover:opacity-80" style="color: var(--theme-accent);">
      <i aria-hidden="true" class="las la-angle-left"></i>{t('appSettings')}
    </Link>
    <Router basepath="/settings">
      <Route path="profile"><Profile /></Route>
      <Route path="kids"><KidsMode /></Route>
      <Route path="devices"><Phones /></Route>
      <Route path="api-keys"><ApiKeys locked={!!$managedBy} /></Route>
      <!-- A child still opens what each person sees of them (spec 001 FR-076, 001 FR-079). -->
      <Route path="people"><SharingSections locked={!!$managedBy} /></Route>
      <Route path="forwarding"><Forwarding /></Route>
      <Route path="safety"><Safety /></Route>
      <Route path="activities"><Activities /></Route>
      <Route path="history"><History /></Route>
      <Route path="appearance"><Appearance /></Route>
      <Route path="server"><Admin part="server" /></Route>
      <Route path="accounts"><Admin part="accounts" /></Route>
      <Route path="/"><Profile /></Route>
    </Router>
  </main>
</div>

<style>
  /* In a narrow window the list is the page, its sections cards of rows;
     beside a topic it is a plain list with the open topic marked. */
  .topics {
    background-color: var(--theme-card-bg);
    border: 1px solid var(--theme-card-border);
    border-radius: var(--theme-radius-md);
    overflow: hidden;
  }
  nav :global(.topic) {
    color: var(--theme-text);
  }
  nav :global(.topic:hover) {
    text-decoration: none;
    background-color: var(--theme-surface0);
  }
  @media (min-width: 768px) {
    .topics {
      background-color: transparent;
      border: 0;
      overflow: visible;
    }
    nav :global(.topic) {
      border-radius: var(--theme-radius-md);
    }
    nav :global(.topic.open) {
      background-color: var(--theme-surface0);
      color: var(--theme-accent);
      font-weight: 600;
    }
  }
</style>
